import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Brain,
  Target,
  Scale,
  TrendingUp,
  CheckCircle,
  Compass,
  Heart,
  Zap,
  Shield,
  Clock,
  Users
} from "lucide-react";

const MentoriaPage = () => {
  const doresEmpreendedor = [
    {
      icon: Brain,
      title: "Solidão nas decisões",
      description: "Sentir que precisa resolver tudo sozinho, sem ter com quem dividir as incertezas e desafios do negócio."
    },
    {
      icon: Scale,
      title: "Desequilíbrio vida-trabalho",
      description: "Dificuldade em separar vida pessoal e profissional, sentindo-se constantemente sobrecarregado."
    },
    {
      icon: Zap,
      title: "Decisões impulsivas",
      description: "Tomar decisões importantes sob pressão, sem tempo ou clareza para avaliar consequências."
    },
    {
      icon: Clock,
      title: "Falta de tempo para crescer",
      description: "Estar tão ocupado apagando incêndios que não consegue planejar o crescimento estratégico."
    },
    {
      icon: Users,
      title: "Dificuldade com a equipe",
      description: "Não saber como liderar, delegar ou desenvolver as pessoas que trabalham com você."
    },
    {
      icon: Heart,
      title: "Ansiedade constante",
      description: "Viver em estado de alerta permanente, com medo de falhar ou de que algo dê errado."
    }
  ];

  const beneficios = [
    {
      icon: Compass,
      title: "Clareza nas decisões",
      description: "Desenvolva uma metodologia para tomar decisões mais conscientes e estratégicas."
    },
    {
      icon: Shield,
      title: "Autogestão emocional",
      description: "Aprenda a lidar com pressão, ansiedade e incertezas de forma saudável."
    },
    {
      icon: Target,
      title: "Foco estratégico",
      description: "Defina prioridades claras e pare de se perder em tarefas que não geram resultado."
    },
    {
      icon: TrendingUp,
      title: "Crescimento sustentável",
      description: "Construa um negócio que cresce sem destruir sua saúde e relações pessoais."
    }
  ];

  const estrutura = [
    {
      title: "Diagnóstico inicial",
      description: "Mapeamento completo do momento atual: desafios, objetivos, pontos fortes e áreas de desenvolvimento."
    },
    {
      title: "Encontros individuais",
      description: "Sessões quinzenais ou semanais de 1h para trabalhar questões estratégicas e comportamentais."
    },
    {
      title: "Plano de ação personalizado",
      description: "Definição de metas claras e ações concretas para evolução em cada área prioritária."
    },
    {
      title: "Suporte contínuo",
      description: "Acompanhamento entre sessões para dúvidas urgentes e momentos de decisão."
    },
    {
      title: "Avaliação de progresso",
      description: "Mensuração periódica de resultados e ajustes no plano conforme evolução."
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="mentoria-page">
      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-brand-terra-humana via-[#C9A07A] to-brand-terra-humana relative overflow-hidden" data-testid="mentoria-hero">
        {/* Subtle texture */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `repeating-linear-gradient(
            45deg,
            transparent,
            transparent 60px,
            rgba(76, 43, 33, 0.1) 60px,
            rgba(76, 43, 33, 0.1) 120px
          )`
        }} />
        
        {/* Decorative lines */}
        <div className="absolute right-12 lg:right-24 top-1/4 bottom-1/4 flex gap-3">
          <div className="w-[2px] h-full bg-brand-neutro/20" />
          <div className="w-[2px] h-full bg-brand-terracota/30 mt-8" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-sans text-sm tracking-[0.15em] text-brand-neutro/60 mb-6 flex items-center gap-3">
              <span className="w-6 h-[1px] bg-brand-neutro/40" />
              MENTORIA PARA EMPREENDEDORES
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-normal text-brand-neutro leading-[1.1] mb-6">
              Empreender não precisa ser{" "}
              <span className="italic">solitário.</span>
            </h1>
            <p className="font-sans text-lg lg:text-xl text-brand-neutro/80 leading-relaxed mb-8">
              Acompanhamento estratégico e comportamental para empreendedores que querem crescer de forma saudável, com mais clareza, equilíbrio e direção.
            </p>
            <a
              href="https://wa.me/5586998325746?text=Olá! Tenho interesse na mentoria para empreendedores. Gostaria de saber mais."
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-sans font-medium"
              data-testid="mentoria-hero-cta"
            >
              Quero saber mais
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* Dores do Empreendedor */}
      <section className="py-20 lg:py-28 bg-brand-base-clara" data-testid="mentoria-dores">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="font-sans text-sm tracking-[0.15em] text-brand-terracota mb-4 flex items-center gap-3 justify-center">
              <span className="w-6 h-[1px] bg-brand-terracota" />
              VOCÊ SE IDENTIFICA?
              <span className="w-6 h-[1px] bg-brand-terracota" />
            </p>
            <h2 className="font-serif text-3xl lg:text-4xl font-normal text-brand-neutro mb-4">
              Os desafios reais de quem empreende
            </h2>
            <p className="font-sans text-brand-muted leading-relaxed">
              Se você reconhece alguma dessas situações, a mentoria pode ser o suporte que você precisa.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {doresEmpreendedor.map((dor, index) => (
              <div 
                key={index}
                className="bg-brand-surface p-6 lg:p-8 rounded-2xl"
                data-testid={`dor-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-brand-terracota/10 flex items-center justify-center mb-4">
                  <dor.icon className="text-brand-terracota" size={24} strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-lg font-medium text-brand-neutro mb-2">
                  {dor.title}
                </h3>
                <p className="font-sans text-sm text-brand-muted leading-relaxed">
                  {dor.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proposta da Mentoria - Dark */}
      <section className="py-20 lg:py-28 bg-brand-neutro" data-testid="mentoria-proposta">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="font-sans text-sm tracking-[0.15em] text-brand-terracota mb-4 flex items-center gap-3">
                <span className="w-6 h-[1px] bg-brand-terracota" />
                A PROPOSTA
              </p>
              <h2 className="font-serif text-3xl lg:text-4xl font-normal text-brand-base-clara mb-6">
                Mentalidade empreendedora com equilíbrio
              </h2>
              <p className="font-sans text-brand-terra-humana leading-relaxed mb-6">
                A mentoria é um espaço de desenvolvimento personalizado onde trabalhamos tanto os aspectos estratégicos quanto comportamentais do empreender.
              </p>
              <p className="font-sans text-brand-terra-humana/80 leading-relaxed mb-8">
                Não é coaching motivacional. É um acompanhamento técnico e humano, fundamentado em psicologia organizacional, para você tomar melhores decisões e construir um negócio que cresce sem te consumir.
              </p>
              <div className="space-y-4">
                {["Abordagem individualizada", "Foco em resultados reais", "Equilíbrio estratégia + comportamento"].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="text-brand-terra-humana flex-shrink-0" size={20} />
                    <span className="font-sans text-brand-base-clara">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-brand-terra-humana/10 p-8 lg:p-10 rounded-2xl">
              <div className="w-12 h-[2px] bg-brand-terra-humana/40 mb-6" />
              <p className="font-serif text-xl lg:text-2xl text-brand-base-clara italic leading-relaxed mb-6">
                "Empreender é mais do que gerir um negócio. É gerir a si mesmo enquanto constrói algo maior."
              </p>
              <p className="font-sans text-brand-terra-humana">
                — Lylian Lopes
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefícios */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-brand-surface to-brand-base-clara" data-testid="mentoria-beneficios">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="font-sans text-sm tracking-[0.15em] text-brand-terracota mb-4 flex items-center gap-3 justify-center">
              <span className="w-6 h-[1px] bg-brand-terracota" />
              BENEFÍCIOS
              <span className="w-6 h-[1px] bg-brand-terracota" />
            </p>
            <h2 className="font-serif text-3xl lg:text-4xl font-normal text-brand-neutro">
              O que você desenvolve
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {beneficios.map((beneficio, index) => (
              <div 
                key={index}
                className="text-center"
                data-testid={`beneficio-${index}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-brand-base-clara flex items-center justify-center mx-auto mb-6 border border-brand-border">
                  <beneficio.icon className="text-brand-terracota" size={28} strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-lg font-medium text-brand-neutro mb-2">
                  {beneficio.title}
                </h3>
                <p className="font-sans text-sm text-brand-muted leading-relaxed">
                  {beneficio.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Estrutura */}
      <section className="py-20 lg:py-28 bg-brand-base-clara" data-testid="mentoria-estrutura">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <p className="font-sans text-sm tracking-[0.15em] text-brand-terracota mb-4 flex items-center gap-3">
                <span className="w-6 h-[1px] bg-brand-terracota" />
                COMO FUNCIONA
              </p>
              <h2 className="font-serif text-3xl lg:text-4xl font-normal text-brand-neutro mb-6">
                Estrutura do acompanhamento
              </h2>
              <p className="font-sans text-brand-muted leading-relaxed mb-8">
                A mentoria é personalizada para cada empreendedor. O formato, frequência e foco são definidos conforme suas necessidades específicas.
              </p>
              <a
                href="https://wa.me/5586998325746?text=Olá! Quero entender melhor como funciona a mentoria para empreendedores."
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-sans font-medium"
                data-testid="mentoria-estrutura-cta"
              >
                Agendar conversa inicial
                <ArrowRight size={18} />
              </a>
            </div>
            <div className="space-y-4">
              {estrutura.map((item, index) => (
                <div 
                  key={index}
                  className="flex gap-4 bg-brand-surface p-6 rounded-xl"
                  data-testid={`estrutura-${index}`}
                >
                  <span className="font-serif text-2xl text-brand-terracota/30 font-semibold flex-shrink-0">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h3 className="font-serif text-lg font-medium text-brand-neutro mb-1">
                      {item.title}
                    </h3>
                    <p className="font-sans text-sm text-brand-muted leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 lg:py-32 bg-brand-terracota relative overflow-hidden" data-testid="mentoria-cta">
        {/* Subtle pattern */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, rgba(232, 223, 198, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 80% 50%, rgba(232, 223, 198, 0.2) 0%, transparent 50%)`
        }} />
        
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl lg:text-4xl xl:text-5xl font-normal text-brand-base-clara mb-6">
            Pronto para empreender com mais equilíbrio?
          </h2>
          <p className="font-sans text-lg text-brand-base-clara/80 leading-relaxed mb-10 max-w-2xl mx-auto">
            Agende uma conversa inicial gratuita. Vamos entender seu momento e ver como a mentoria pode te ajudar.
          </p>
          <a
            href="https://wa.me/5586998325746?text=Olá! Gostaria de agendar uma conversa sobre a mentoria para empreendedores."
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center justify-center gap-2 bg-brand-base-clara text-brand-neutro hover:bg-white px-8 py-4 rounded-xl font-sans font-medium transition-premium"
            data-testid="mentoria-cta-button"
          >
            Agendar Conversa Gratuita
            <ArrowRight size={18} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default MentoriaPage;
